/* eslint-disable @typescript-eslint/ban-ts-comment */
import type { ChakraProps } from '@chakra-ui/react';
import { BigNumber } from '@ethersproject/bignumber';
import { MaxInt256 } from '@ethersproject/constants';
import { formatUnits } from '@ethersproject/units';
import type { UseQueryOptions, UseQueryResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { LPTokenIcon } from '@ui/components/LPTokenIcon';
import { TokenIcon } from '@ui/components/TokenIcon';
import { useBalance } from '@ui/hooks/useBalance';
import { type CoinMode, COIN_MODE, useCoinMode } from '@ui/hooks/useCoinMode';
import { useCoinUsdValue } from '@ui/hooks/useCoinUsdValue';
import useFerroLpBalance from '@ui/hooks/useFerroLpBalance';
import { useLiquidCoinBalance } from '@ui/hooks/useLiquidCoinBalance';
import {
  useCoinAmountFromLiquidCoin,
  useLiquidCoinUsd,
} from '@ui/hooks/useLiquidCoinUsd';
import useLpReceiveValue from '@ui/hooks/useLpReceiveValue';
import useLpUsdValue from '@ui/hooks/useLpUsdValue';
import useWcroBalance from '@ui/hooks/useWcroBalance';
import { useWcroUsdValue } from '@ui/hooks/useWcroUsdPrice';
import { useVenoSDK } from '@ui/providers/VenoSDKProvider';
import {
  type ContractReceipt,
  type VenoSDK,
  type VenoTransaction,
  expandDecimals,
} from '@veno-app/sdk';
import { currentWallet } from '@veno-app/wallet';
import type { FC } from 'react';
import { useMemo } from 'react';

import bigNumberToNumber from './bigNumberToNumber';
import { parseValue } from './format';
import { gardenTokenDict } from './garden';

export type LiquidityStrategyView = 'deposit' | 'withdraw' | 'dashboard';

export type LiquidityStrategyDepositToken = 'cro' | 'wcro' | 'lcro' | 'ferroLP';

export const LiquidityStrategyTokenDict: Record<
  LiquidityStrategyDepositToken,
  {
    name: string;
    Icon: FC<ChakraProps>;
    vaultTokenName: string;
    maxDecimals: number;
    decimals: number;
  }
> = {
  cro: {
    name: 'CRO',
    Icon: (props) => <TokenIcon token="cro" {...props} />,
    vaultTokenName: 'vLCRO-CRO',
    maxDecimals: 8,
    decimals: COIN_MODE.cro.decimals,
  },
  wcro: {
    name: 'WCRO',
    Icon: (props) => <TokenIcon token="cro" {...props} />,
    vaultTokenName: 'vLCRO-CRO',
    maxDecimals: 8,
    decimals: COIN_MODE.cro.decimals,
  },
  lcro: {
    name: 'LCRO',
    Icon: (props) => <TokenIcon token="lcro" {...props} />,
    vaultTokenName: 'vLCRO',
    maxDecimals: 8,
    decimals: COIN_MODE.cro.liquidDecimals,
  },
  ferroLP: {
    name: 'LCRO-CRO-LP',
    Icon: ({ width, height, ...props }) => (
      <LPTokenIcon
        coinMode="cro"
        gardenType="FERRO"
        iconWidth={width}
        iconHeight={height}
        {...props}
      />
    ),
    vaultTokenName: 'vLCRO-CRO',
    maxDecimals: 8,
    decimals: gardenTokenDict.cro.FERRO.decimals,
  },
};

const getReceivedAmount = (
  sdk: VenoSDK,
  receipt: ContractReceipt,
  _receiver: string,
): BigNumber | undefined => {
  const targetEvent = receipt.logs
    .map((log) => {
      try {
        return sdk.WcroYVault.interface.parseLog(log);
      } catch (e) {
        // nothing
      }
    })
    .find((event) => {
      return Boolean(
        event && (event.name === 'Deposit' || event.name === 'Withdraw'),
      );
    });

  return (targetEvent?.args?.amount as BigNumber) || undefined;
};

type VaultSdk = {
  contractAddress: string;
  balanceOf: (account: string) => Promise<BigNumber>;
  exchangeRate: () => Promise<BigNumber>;
  deposit: (amount: BigNumber, account: string) => Promise<VenoTransaction>;
  withdraw: (amount: BigNumber, account: string) => Promise<VenoTransaction>;
  getDepositReceivedAmount: (
    receipt: ContractReceipt,
    receiver: string,
  ) => BigNumber | undefined;
  getWithdrawReceivedAmount: (
    receipt: ContractReceipt,
    receiver: string,
  ) => BigNumber | undefined;
  shouldApproveWhenWithdraw?: (
    account: string,
    amount: BigNumber,
  ) => Promise<boolean>;
  approveWhenWithdraw?: (amount: BigNumber) => Promise<VenoTransaction>;
  getTvl: () => Promise<number>;
};

export const useVaultSdk = (
  token: LiquidityStrategyDepositToken,
): VaultSdk | null => {
  const sdk = useVenoSDK();

  const _getReceivedAmount = (receipt: ContractReceipt, receiver: string) =>
    getReceivedAmount(sdk, receipt, receiver);

  if (token === 'cro') {
    return {
      contractAddress: sdk.CroYVault.contractAddress,
      balanceOf: async (account) => {
        return sdk.WcroYVault.balanceOf(account);
      },
      exchangeRate: async () => {
        return sdk.WcroYVault.exchangeRate();
      },
      deposit: async (amount) => {
        return sdk.CroYVault.deposit(amount);
      },
      withdraw: async (amount) => {
        return sdk.CroYVault.withdraw(amount);
      },
      getDepositReceivedAmount: _getReceivedAmount,
      getWithdrawReceivedAmount: _getReceivedAmount,
      shouldApproveWhenWithdraw: async (account, amount) => {
        const allowance = await sdk.WcroYVault.allowance(
          account,
          sdk.CroYVault.contractAddress,
        );
        return allowance.lt(amount);
      },
      approveWhenWithdraw: async (amount) => {
        return sdk.WcroYVault.approve(sdk.CroYVault.contractAddress, amount);
      },
      getTvl: async () => 0,
    };
  }

  const commonVaultSdkMap: Record<
    Exclude<LiquidityStrategyDepositToken, 'cro'>,
    typeof sdk.WcroYVault
  > = {
    wcro: sdk.WcroYVault,
    ferroLP: sdk.FerroLpYVault,
    lcro: sdk.LcroYVault,
  };

  const newVault: typeof sdk.WcroYVault = Object.create(
    commonVaultSdkMap[token],
  );

  return Object.assign(newVault, {
    getDepositReceivedAmount: _getReceivedAmount,
    getWithdrawReceivedAmount: _getReceivedAmount,
  });
};

type TokenSdk = {
  allowance: (account: string, contractAddress: string) => Promise<BigNumber>;
  approve: (
    contractAddress: string,
    amount: BigNumber,
  ) => Promise<VenoTransaction>;
};

export const useTokenSdk = (
  token: LiquidityStrategyDepositToken,
): TokenSdk | null => {
  const sdk = useVenoSDK();

  return useMemo(() => {
    if (token === 'cro') {
      return {
        allowance: async () => {
          return MaxInt256;
        },
        approve: async () => {
          return {
            txHash: '',
            txReceiptPromise: new Promise((resolve) =>
              resolve({} as ContractReceipt),
            ),
          };
        },
      };
    } else if (token === 'wcro') {
      return sdk.Wcro;
    } else if (token === 'lcro') {
      return sdk.LiquidCro;
    } else if (token === 'ferroLP') {
      return sdk.FerroLpToken;
    }
    return null;
  }, [token, sdk.FerroLpToken, sdk.LiquidCro, sdk.Wcro]);
};

export const useVaultBalance = (
  token: LiquidityStrategyDepositToken,
  options: UseQueryOptions<BigNumber> = {},
): UseQueryResult<BigNumber> => {
  const vaultSdk = useVaultSdk(token);
  const account = currentWallet.useAccount();

  return useQuery<BigNumber>(
    ['useVaultBalance', token, account],
    async () => {
      if (!vaultSdk || !account) {
        return BigNumber.from('0');
      }

      return vaultSdk.balanceOf(account);
    },
    options,
  );
};

export const useVaultExchangeRate = (
  token: LiquidityStrategyDepositToken,
  options: UseQueryOptions<number | undefined> = {},
) => {
  const vaultSdk = useVaultSdk(token);

  return useQuery<number | undefined>(
    ['useVaultExchangeRate', token],
    async () => {
      if (!vaultSdk) {
        return;
      }

      return bigNumberToNumber(await vaultSdk.exchangeRate());
    },
    options,
  );
};

export const useTokenAssetsFromShares = (
  token: LiquidityStrategyDepositToken,
  shares: BigNumber | undefined | null,
) => {
  const exchangeRateRet = useVaultExchangeRate(token);

  const precision = 1_000_000;
  return shares && exchangeRateRet.data
    ? shares.mul(Math.round(exchangeRateRet.data * precision)).div(precision)
    : undefined;
};

export const useSharesFromTokenAssets = (
  token: LiquidityStrategyDepositToken,
  assets: BigNumber | undefined | null,
) => {
  const exchangeRateRet = useVaultExchangeRate(token);

  const precision = 1_000_000;
  return assets && exchangeRateRet.data
    ? assets.mul(precision).div(Math.round(exchangeRateRet.data * precision))
    : undefined;
};

export const useDepositTokenBalances = (
  options?: UseQueryOptions<BigNumber>,
): Record<LiquidityStrategyDepositToken, UseQueryResult<BigNumber>> => {
  const croBalanceRet = useBalance();

  const wcroBalanceRet = useWcroBalance(options);

  const lcroBalanceRet = useLiquidCoinBalance({
    ...options,
    coinMode: 'cro',
  });

  const ferroLpBalanceRet = useFerroLpBalance({
    ...options,
    coinMode: 'cro',
  });

  return {
    cro: {
      ...croBalanceRet,
      data:
        typeof croBalanceRet.data === 'undefined'
          ? undefined
          : parseValue(String(croBalanceRet.data), 18),
    } as UseQueryResult<BigNumber>,
    wcro: wcroBalanceRet,
    lcro: lcroBalanceRet,
    ferroLP: ferroLpBalanceRet,
  };
};

export const useDepositTokenBalanceUsd = ({
  token,
  amount,
}: {
  token: LiquidityStrategyDepositToken;
  amount: BigNumber | undefined;
}): BigNumber | undefined => {
  const croBalanceUsdRet = useCoinUsdValue(amount, 'cro');

  const wcroBalanceUsdRet = useWcroUsdValue(amount);

  const lcroBalanceUsdRet = useLiquidCoinUsd({
    coinMode: 'cro',
    amount,
  });

  const ferroLpBalanceUsd = useLpUsdValue(
    'FERRO',
    amount ? formatUnits(amount) : '',
  );

  let ferroLpBalanceUsdBn: BigNumber | undefined = undefined;

  try {
    ferroLpBalanceUsdBn =
      typeof ferroLpBalanceUsd === 'number'
        ? parseValue(String(ferroLpBalanceUsd), 18)
        : undefined;
  } catch (e) {
    ferroLpBalanceUsdBn = BigNumber.from('0');
  }

  const dict: Record<LiquidityStrategyDepositToken, BigNumber | undefined> = {
    cro: croBalanceUsdRet.data,
    wcro: wcroBalanceUsdRet.data,
    lcro: lcroBalanceUsdRet.data,
    ferroLP: ferroLpBalanceUsdBn,
  };

  return dict[token];
};

export const useStrategyApy = () => {
  /**
   * todo cover.lv
   */
  return useQuery(['useStrategyApy'], () => {
    return 0.76;
  });
};

const useCoinAmountFromFerroLp = (ferroLpAmount: BigNumber | undefined) => {
  const coinMode = useCoinMode();

  const { exchangeRate } = useLpReceiveValue(
    coinMode,
    'FERRO',
    expandDecimals(gardenTokenDict[coinMode].FERRO.decimals),
  );

  const precision = 1_000_000;
  const lcroAmount =
    ferroLpAmount && typeof exchangeRate === 'number'
      ? ferroLpAmount.mul(precision).div(Math.round(exchangeRate * precision))
      : undefined;

  return useCoinAmountFromLiquidCoin({
    coinMode: 'cro',
    amount: lcroAmount,
  });
};

export const useOwnedDepositCoinAmount = () => {
  const coinAmountDict: Record<
    Exclude<LiquidityStrategyDepositToken, 'cro'>,
    BigNumber | undefined
  > = {
    wcro: useTokenAssetsFromShares('wcro', useVaultBalance('wcro').data),
    lcro: useCoinAmountFromLiquidCoin({
      coinMode: 'cro',
      amount: useTokenAssetsFromShares('lcro', useVaultBalance('lcro').data),
    }),
    ferroLP: useCoinAmountFromFerroLp(
      useTokenAssetsFromShares('ferroLP', useVaultBalance('ferroLP').data),
    ),
  };

  const isLoading = Object.values(coinAmountDict).some(
    (amount) => typeof amount === 'undefined',
  );

  if (isLoading) {
    return;
  }

  return Object.values(coinAmountDict).reduce<BigNumber>(
    (sum, amount) => sum.add(amount ?? BigNumber.from('0')),
    BigNumber.from('0'),
  );
};

export const useStrategyTvl = (coinMode?: CoinMode) => {
  const sdkMap: Record<
    Exclude<LiquidityStrategyDepositToken, 'cro'>,
    VaultSdk | null
  > = {
    wcro: useVaultSdk('wcro'),
    ferroLP: useVaultSdk('ferroLP'),
    lcro: useVaultSdk('lcro'),
  };

  // TODO update when we have an atom strategy
  return useQuery(['useStrategyTvl', coinMode], async () => {
    const sdks = Object.values(sdkMap);

    const tvlList = await Promise.all(
      sdks.map((sdk) => {
        return sdk ? sdk.getTvl() : 0;
      }),
    );

    return tvlList.reduce((sum, tvl) => sum + tvl, 0);
  });
};
